import { useMutation } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { type Questionnaire } from "api-client/legacy-models";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { Button } from "ui";

import { trpcClient } from "src/trpc";

import { useQuestionnaireSubmissionContext } from "./QuestionnaireSubmissionContext";
import { SubmissionItem } from "./SubmissionItem";

export const QuestionnaireSubmissionFill = observer(
  function QuestionnaireSubmissionFill() {
    const { t } = useTranslation();
    const router = useRouter();

    const questionnaireSubmissionContext = useQuestionnaireSubmissionContext();
    const submitQuestionnaireMutation = useMutation({
      mutationFn: (arg: Questionnaire.SubmitSubmission.Input) =>
        trpcClient.questionnaire.submitSubmission.mutate(arg),
    });

    const isSubmitting = submitQuestionnaireMutation.isPending;

    const disabled =
      questionnaireSubmissionContext.isSubmitted ||
      !questionnaireSubmissionContext.wasCreatedBySameUser;

    return (
      <div className="flex flex-col">
        <div className="w-full flex flex-col gap-4">
          {questionnaireSubmissionContext.rootItems.map((item) => {
            return (
              <SubmissionItem
                key={item.id}
                disabled={disabled}
                itemId={item.id}
              />
            );
          })}
          {disabled ? null : (
            <div className="flex items-center justify-center gap-2">
              <Button
                disabled={
                  isSubmitting || !!questionnaireSubmissionContext.isSubmitted
                }
                loading={isSubmitting}
                onClick={async () => {
                  const data = await submitQuestionnaireMutation.mutateAsync({
                    questionnaireSubmissionId:
                      questionnaireSubmissionContext.questionnaireSubmission.id,
                  });
                  questionnaireSubmissionContext.setSubmitSubmissionResult(
                    data,
                  );

                  if (
                    data?.type === "error" &&
                    "requiredItemsWithoutAnswer" in data
                  ) {
                    const item = document.querySelector(
                      `[data-questionnaire-item-id="${data.requiredItemsWithoutAnswer[0]}"]`,
                    );
                    item?.scrollIntoView({
                      behavior: "smooth",
                      block: "center",
                    });
                  } else {
                    router.invalidate();
                  }
                }}
              >
                {t("questionnaireSubmission.submit")}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  },
);
